import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";
import { toast, Toaster } from "sonner";
import {
  Users, Package, DollarSign, AlertTriangle, TrendingUp, Wallet,
  Truck, Shield, ShieldCheck, Bell, Handshake, Layers, Plus, Copy,
  Wand2, ArrowRight, X, Check, Loader2, ExternalLink, RefreshCw, Trash2,
  ReceiptText, Download, Send,
} from "lucide-react";

// =============================================================================
// SellePack — Unified Admin Hub
// One page, tabbed sections + a wizard rail. Everything an admin needs in 1 URL.
// =============================================================================

const TABS = [
  { id: "overview",      label: "Overview",      icon: TrendingUp },
  { id: "revenue",       label: "Revenue",       icon: DollarSign },
  { id: "reports",       label: "Reports",       icon: ReceiptText },
  { id: "users",         label: "Users",         icon: Users },
  { id: "partners",      label: "Partners",      icon: Handshake },
  { id: "fraud",         label: "SelleVerify",   icon: Shield },
  { id: "carriers",      label: "Carriers",      icon: Truck,        link: "/dashboard/admin/shipping" },
  { id: "insurance",     label: "Insurance",     icon: ShieldCheck,  link: "/dashboard/admin/insurance" },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "pricing",       label: "Pricing",       icon: Layers },
];

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [activeWizard, setActiveWizard] = useState(null); // "carrier" | "partner" | "fraud" | "setup"

  const loadOverview = async () => {
    try {
      const { data } = await api.get("/admin/overview");
      setOverview(data);
    } catch (e) { /* silent — banner will show 0s */ }
  };
  useEffect(() => { loadOverview(); }, []);

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Admin · Command center</div>
          <h1 data-testid="admin-hub-title" className="font-display text-3xl md:text-4xl font-black tracking-tighter">Admin Hub</h1>
        </div>
        <button data-testid="refresh-overview" onClick={loadOverview}
          className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 border border-zinc-300 rounded-sm px-3 py-1.5">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <StatStrip o={overview} />

      <div className="grid lg:grid-cols-[1fr_280px] gap-6 mt-6">
        {/* main content */}
        <div className="min-w-0">
          {/* tab bar */}
          <div className="flex flex-wrap gap-1 border-b border-zinc-200 mb-5">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              const cls = `inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ${
                active
                  ? "border-[var(--brand-accent)] text-zinc-900 font-medium"
                  : "border-transparent text-zinc-500 hover:text-zinc-900"
              }`;
              if (t.link) {
                return (
                  <Link key={t.id} to={t.link} data-testid={`tab-${t.id}`} className={cls}>
                    <Icon size={14} />
                    {t.label}
                    <ExternalLink size={10} className="opacity-60" />
                  </Link>
                );
              }
              return (
                <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)} className={cls}>
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {tab === "overview"      && <OverviewTab o={overview} onWizard={setActiveWizard} />}
          {tab === "revenue"       && <RevenueTab />}
          {tab === "reports"       && <ReportsTab />}
          {tab === "users"         && <UsersTab />}
          {tab === "partners"      && <PartnersTab onLaunchWizard={() => setActiveWizard("partner")} />}
          {tab === "fraud"         && <FraudToolsTab />}
          {tab === "notifications" && <NotificationsTab />}
          {tab === "pricing"       && <PricingTab />}
        </div>

        {/* wizard rail */}
        <WizardRail onLaunch={setActiveWizard} />
      </div>

      {activeWizard === "partner" && <PartnerWizard onClose={() => { setActiveWizard(null); loadOverview(); }} />}
      {activeWizard === "carrier" && <CarrierWizard onClose={() => setActiveWizard(null)} />}
      {activeWizard === "fraud"   && <FraudWizard onClose={() => setActiveWizard(null)} />}
      {activeWizard === "setup"   && <FirstRunWizard onClose={() => setActiveWizard(null)} />}
    </DashboardLayout>
  );
}

// -------------------- Top stats strip --------------------
function StatStrip({ o }) {
  const s = o || {};
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <Stat label="Users" value={s.total_users ?? "—"} icon={Users} />
      <Stat label="Pending approval" value={s.pending_users ?? "—"} icon={AlertTriangle} warn={(s.pending_users || 0) > 0} />
      <Stat label="Shipments" value={s.total_shipments ?? "—"} icon={Package} />
      <Stat label="GMV" value={fmtUsd(s.gmv)} icon={DollarSign} />
      <Stat label="Platform revenue" value={fmtUsd(s.platform_surcharge_total)} icon={TrendingUp} accent />
      <Stat label="Wallet float" value={fmtUsd(s.wallet_float)} icon={Wallet} />
    </div>
  );
}

function Stat({ label, value, icon: Icon, warn, accent }) {
  const colors = warn ? "border-amber-300 bg-amber-50" : accent ? "border-emerald-300 bg-emerald-50" : "border-zinc-200 bg-white";
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g,"-")}`} className={`border rounded-sm p-3 ${colors}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</div>
        <Icon size={12} className={warn ? "text-amber-500" : accent ? "text-emerald-600" : "text-zinc-400"} />
      </div>
      <div className="font-display text-xl md:text-2xl font-black tracking-tighter mt-1 truncate">{value}</div>
    </div>
  );
}

// -------------------- Wizard rail --------------------
function WizardRail({ onLaunch }) {
  const launchers = [
    { id: "setup",   t: "First-run setup",     d: "Walk through Carriers → Fraud → Partners.", icon: Wand2 },
    { id: "carrier", t: "Connect a carrier",   d: "Paste key, test rate, set markup, enable.",  icon: Truck },
    { id: "partner", t: "Onboard a partner",   d: "Generate referral link + commission.",        icon: Handshake },
    { id: "fraud",   t: "Add a fraud tool",    d: "Vendor, key, per-check surcharge, test.",     icon: Shield },
  ];
  return (
    <aside className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Wizards</div>
      {launchers.map((l) => {
        const Icon = l.icon;
        return (
          <button key={l.id} data-testid={`wizard-${l.id}`} onClick={() => onLaunch(l.id)}
            className="w-full text-left bg-white border border-zinc-200 rounded-sm p-3 hover:border-[var(--brand-accent)] hover:shadow-sm transition-all group">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-sm bg-zinc-900 text-white flex items-center justify-center group-hover:brand-bg transition-colors">
                <Icon size={14} />
              </div>
              <div className="flex-1 font-display font-bold text-sm">{l.t}</div>
              <ArrowRight size={14} className="text-zinc-400 group-hover:text-zinc-900" />
            </div>
            <div className="text-[11px] text-zinc-500 mt-1.5">{l.d}</div>
          </button>
        );
      })}
    </aside>
  );
}

// -------------------- Tabs --------------------
function OverviewTab({ o, onWizard }) {
  return (
    <div className="space-y-5">
      <div className="bg-white border border-zinc-200 rounded-sm p-5">
        <div className="font-display font-bold text-lg mb-2">Welcome back</div>
        <p className="text-sm text-zinc-600 max-w-2xl">
          Use the tabs above for deep configuration. The wizards on the right are the fastest way to
          add a carrier, onboard a partner, or wire a fraud tool — each one ends with a live test
          so you can see it work before going live.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <ShortCut to="/dashboard/admin/shipping" label="Shipping providers (Ship 1–4)" />
          <ShortCut to="/dashboard/admin/insurance" label="Insurance & risk policy" />
          <ShortCut to="#" onClick={() => onWizard("partner")} label="Onboard a new partner" />
          <ShortCut to="#" onClick={() => onWizard("setup")} label="Run first-time setup" />
        </div>
      </div>
    </div>
  );
}

function ShortCut({ to, label, onClick }) {
  const cls = "flex items-center justify-between border border-zinc-200 rounded-sm px-3 py-2.5 text-sm hover:border-[var(--brand-accent)] hover:bg-zinc-50";
  if (onClick) return <button onClick={onClick} className={cls}><span>{label}</span><ArrowRight size={14} className="text-zinc-400" /></button>;
  return <Link to={to} className={cls}><span>{label}</span><ArrowRight size={14} className="text-zinc-400" /></Link>;
}

function RevenueTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/revenue/summary?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load revenue");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);

  if (loading) return <div className="text-sm text-zinc-500 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>;
  if (!data) return <div className="text-sm text-zinc-500">No data.</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-xs uppercase tracking-wider text-zinc-500">Window</div>
        {[7, 30, 90, 365].map((d) => (
          <button key={d} data-testid={`revenue-window-${d}`} onClick={() => setDays(d)}
            className={`text-xs px-2.5 py-1 rounded-sm border ${days === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}>
            {d}d
          </button>
        ))}
        <div className="ml-auto text-sm">
          <span className="text-zinc-500">Total revenue: </span>
          <span data-testid="revenue-total" className="font-mono-plex font-bold text-emerald-700">{fmtUsd(data.total_revenue)}</span>
          <span className="text-zinc-400 ml-2">· {data.total_shipments} shipments</span>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-5">
        <div className="font-display font-bold mb-3">Revenue by program (slot)</div>
        {!data.by_slot.length ? <div className="text-sm text-zinc-500">No shipments in window.</div> : (
          <div className="space-y-2">
            {data.by_slot.map((s) => {
              const max = Math.max(...data.by_slot.map((x) => x.revenue), 1);
              const pct = Math.max(2, (s.revenue / max) * 100);
              return (
                <div key={s.slot} className="flex items-center gap-3">
                  <div className="w-20 shrink-0 font-display font-bold text-sm">{s.label}</div>
                  <div className="flex-1 bg-zinc-100 rounded-sm h-7 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 brand-bg" style={{ width: `${pct}%` }} />
                    <div className="absolute inset-0 flex items-center px-2 text-xs font-mono-plex text-white mix-blend-difference">
                      {fmtUsd(s.revenue)} · {s.shipments} ship
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-5">
        <div className="font-display font-bold mb-3">Top users (revenue contribution)</div>
        {!data.top_users.length ? <div className="text-sm text-zinc-500">No data.</div> : (
          <div className="overflow-x-auto -mx-5 px-5">
            <table className="w-full text-sm min-w-[480px]">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <th className="text-left py-2">User</th>
                  <th className="text-left">Email</th>
                  <th className="text-right">Shipments</th>
                  <th className="text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {data.top_users.map((u) => (
                  <tr key={u.user_id} className="border-b border-zinc-100">
                    <td className="py-2">{u.name}</td>
                    <td className="text-zinc-600">{u.email}</td>
                    <td className="text-right font-mono-plex">{u.shipments}</td>
                    <td className="text-right font-mono-plex font-bold text-emerald-700">{fmtUsd(u.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); } catch {}
  };
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    if (action === "reject" && !window.confirm("Reject this application?")) return;
    try { await api.post(`/admin/users/${id}/${action}`); toast.success(action[0].toUpperCase()+action.slice(1)+"d"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setCustomerType = async (id, customer_type) => {
    try { await api.put(`/admin/users/${id}/customer-type`, { customer_type }); toast.success("Channel updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const pending = users.filter((u) => u.approval_status === "pending");
  const others = users.filter((u) => u.approval_status !== "pending");
  return (
    <div className="space-y-5">
      {pending.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-sm p-4">
          <div className="font-display font-bold text-amber-900 mb-2">{pending.length} pending approval{pending.length===1?"":"s"}</div>
          <UsersTable users={pending} onAct={act} onRole={setRole} onChannel={setCustomerType} />
        </div>
      )}
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <div className="font-display font-bold mb-2">All users ({others.length})</div>
        <UsersTable users={others} onAct={act} onRole={setRole} onChannel={setCustomerType} />
      </div>
    </div>
  );
}

function UsersTable({ users, onAct, onRole, onChannel }) {
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-sm min-w-[820px]">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="text-left py-2">Name</th>
            <th className="text-left">Email</th>
            <th className="text-left">Channel</th>
            <th className="text-left">Role</th>
            <th className="text-left">Status</th>
            <th className="text-right">Wallet</th>
            <th className="text-right px-4">Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.user_id} className="border-b border-zinc-100" data-testid={`user-row-${u.user_id}`}>
              <td className="py-2">{u.name}</td>
              <td className="text-zinc-600">{u.email}</td>
              <td>
                <select data-testid={`channel-select-${u.user_id}`} value={u.customer_type || "retail"}
                  onChange={(e) => onChannel(u.user_id, e.target.value)}
                  className={`border rounded-sm px-2 py-1 text-xs uppercase tracking-wider ${
                    (u.customer_type === "wholesale")
                      ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                      : "bg-white border-zinc-300 text-zinc-700"
                  }`}>
                  <option value="retail">Retail</option>
                  <option value="wholesale">Wholesale</option>
                </select>
              </td>
              <td>
                <select value={u.role} onChange={(e) => onRole(u.user_id, e.target.value)}
                  className="border border-zinc-300 rounded-sm px-2 py-1 text-xs uppercase tracking-wider">
                  {[["shipper","Shipper"],["reseller","Reseller"],["admin","Admin"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </td>
              <td>
                {u.approval_status === "approved" && <Tag className="bg-emerald-50 text-emerald-700">Approved</Tag>}
                {u.approval_status === "pending"  && <Tag className="bg-amber-50 text-amber-700">Pending</Tag>}
                {u.approval_status === "rejected" && <Tag className="bg-red-50 text-red-700">Rejected</Tag>}
                {!u.approval_status               && <Tag className="bg-zinc-100 text-zinc-600">—</Tag>}
              </td>
              <td className="text-right font-mono-plex">{fmtUsd(u.wallet_balance)}</td>
              <td className="text-right px-4">
                {u.approval_status === "pending" && (
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`approve-user-${u.user_id}`} onClick={() => onAct(u.user_id,"approve")}
                      className="brand-bg text-white text-[10px] px-2 py-1 rounded-sm hover:opacity-90">Approve</button>
                    <button data-testid={`reject-user-${u.user_id}`} onClick={() => onAct(u.user_id,"reject")}
                      className="border border-red-300 text-red-700 text-[10px] px-2 py-1 rounded-sm hover:bg-red-50">Reject</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tag({ children, className = "" }) {
  return <span className={`px-2 py-0.5 rounded-sm text-[10px] font-medium uppercase tracking-wider ${className}`}>{children}</span>;
}

function PartnersTab({ onLaunchWizard }) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/partners"); setPartners(data); }
    catch (e) { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this partner? Existing commission history is preserved.")) return;
    try { await api.delete(`/admin/partners/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const update = async (id, patch) => {
    try { await api.put(`/admin/partners/${id}`, patch); toast.success("Saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  if (loading) return <div className="text-sm text-zinc-500"><Loader2 className="animate-spin inline" size={14} /> Loading…</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display font-bold text-lg">Partners ({partners.length})</div>
          <div className="text-sm text-zinc-600 mt-0.5 max-w-2xl">
            Partners earn commission on every shipment booked by a user that signed up
            with their referral code. Choose <b>percentage</b> (of platform surcharge) or
            <b> flat</b> ($ per shipment) per partner.
          </div>
        </div>
        <button data-testid="new-partner-btn" onClick={onLaunchWizard}
          className="brand-bg text-white rounded-sm px-3 py-2 text-sm inline-flex items-center gap-1 hover:opacity-90">
          <Plus size={14} /> New partner
        </button>
      </div>

      {!partners.length ? (
        <div className="bg-white border border-dashed border-zinc-300 rounded-sm p-8 text-center text-sm text-zinc-500">
          No partners yet. Click <b>New partner</b> to launch the onboarding wizard.
        </div>
      ) : (
        <div className="space-y-3">
          {partners.map((p) => <PartnerRow key={p.partner_id} p={p} onUpdate={update} onDelete={del} />)}
        </div>
      )}
    </div>
  );
}

function PartnerRow({ p, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    commission_type: p.commission_type,
    commission_rate: String(p.commission_rate),
    status: p.status,
  });
  useEffect(() => {
    setForm({ commission_type: p.commission_type, commission_rate: String(p.commission_rate), status: p.status });
  }, [p.commission_type, p.commission_rate, p.status]);
  const baseUrl = typeof window !== "undefined" ? `${window.location.origin}/register?ref=${p.referral_code}` : "";
  const save = () => {
    onUpdate(p.partner_id, {
      commission_type: form.commission_type,
      commission_rate: parseFloat(form.commission_rate) || 0,
      status: form.status,
    });
    setEditing(false);
  };
  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success("Copied"); };

  return (
    <div data-testid={`partner-row-${p.partner_id}`} className="bg-white border border-zinc-200 rounded-sm p-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-bold">{p.name}</div>
            <Tag className={p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}>{p.status}</Tag>
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {p.email}{p.company ? ` · ${p.company}` : ""}
          </div>
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <code className="text-[11px] bg-zinc-900 text-white px-2 py-1 rounded-sm font-mono-plex">{p.referral_code}</code>
            <button onClick={() => copy(p.referral_code)} className="text-zinc-400 hover:text-zinc-900" title="Copy code">
              <Copy size={12} />
            </button>
            <button onClick={() => copy(baseUrl)} className="text-[11px] text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 underline">
              <Copy size={11} /> referral link
            </button>
          </div>
        </div>

        <div className="flex items-center gap-3 text-right text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Earned</div>
            <div className="font-mono-plex font-bold text-emerald-700">{fmtUsd(p.lifetime_earned)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Users</div>
            <div className="font-mono-plex font-bold">{p.referred_users}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Commissions</div>
            <div className="font-mono-plex font-bold">{p.lifetime_commissions}</div>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-zinc-100 flex items-end gap-3 flex-wrap">
        {!editing ? (
          <>
            <div className="text-sm">
              <span className="text-zinc-500">Pays </span>
              {p.commission_type === "percent" ? (
                <span className="font-mono-plex font-bold">{p.commission_rate}% of platform surcharge</span>
              ) : (
                <span className="font-mono-plex font-bold">{fmtUsd(p.commission_rate)} per shipment (flat)</span>
              )}
            </div>
            <button data-testid={`edit-partner-${p.partner_id}`} onClick={() => setEditing(true)}
              className="text-xs border border-zinc-300 rounded-sm px-2.5 py-1.5 hover:bg-zinc-50">Edit</button>
            <button onClick={() => onUpdate(p.partner_id, { status: p.status === "active" ? "paused" : "active" })}
              className="text-xs border border-zinc-300 rounded-sm px-2.5 py-1.5 hover:bg-zinc-50">
              {p.status === "active" ? "Pause" : "Activate"}
            </button>
            <button onClick={() => onDelete(p.partner_id)}
              className="text-xs border border-red-300 text-red-700 rounded-sm px-2.5 py-1.5 hover:bg-red-50 inline-flex items-center gap-1">
              <Trash2 size={12} /> Delete
            </button>
          </>
        ) : (
          <>
            <label className="block text-xs">
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Type</div>
              <select value={form.commission_type} onChange={(e) => setForm({ ...form, commission_type: e.target.value })}
                className="border border-zinc-300 rounded-sm px-2 py-1.5 text-sm bg-white">
                <option value="percent">% of platform surcharge</option>
                <option value="flat">Flat $ per shipment</option>
              </select>
            </label>
            <label className="block text-xs">
              <div className="text-zinc-500 uppercase tracking-wider mb-1">Rate</div>
              <input type="number" step="0.01" min="0" value={form.commission_rate}
                onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                className="w-28 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm font-mono-plex" />
            </label>
            <button onClick={save} className="brand-bg text-white text-xs px-3 py-1.5 rounded-sm">Save</button>
            <button onClick={() => setEditing(false)} className="border border-zinc-300 text-xs px-3 py-1.5 rounded-sm">Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

function FraudToolsTab() {
  const [data, setData] = useState(null);
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/smart-services/selle-verify");
      setData(data);
      setPrice(String(data.price));
    } catch (e) { toast.error("Failed to load SelleVerify"); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/smart-services/selle-verify", { price: parseFloat(price) || 0 });
      setData(data);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!data) return <div className="text-sm text-zinc-500 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>;

  const vendors = data.vendors || [];
  const status = data.vendor_status || {};

  return (
    <div className="space-y-4">
      <div>
        <div className="font-display font-bold text-lg">SelleVerify · Pre-shipment fraud screen</div>
        <p className="text-sm text-zinc-600 mt-0.5 max-w-2xl">
          A single Smart Service users activate to unlock the full fraud stack
          on every shipment. One flat surcharge per check — same model as
          SelleSend. Users opt in; you set the price.
        </p>
      </div>

      <div data-testid="selleverify-config-card" className="bg-white border border-zinc-200 rounded-sm p-5 grid sm:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#0052FF] text-white text-[10px] uppercase tracking-[0.2em] rounded-sm">
              SelleVerify
            </span>
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Smart service · code: SELLE_VERIFY</span>
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Price per check (USD)</div>
            <div className="inline-flex items-center gap-1">
              <span className="text-zinc-400">$</span>
              <input data-testid="selleverify-price" type="number" step="0.01" min="0" value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-32 border border-zinc-300 rounded-sm px-2 py-1.5 font-mono-plex text-base" />
            </div>
          </label>
          <div className="text-[11px] text-zinc-500 mt-2">
            Charged to the user's wallet on every pre-shipment fraud check (only when SelleVerify is active on their account).
          </div>
        </div>
        <button data-testid="selleverify-save" disabled={saving || parseFloat(price) === data.price}
          onClick={save}
          className="brand-bg text-white text-sm px-4 py-2 rounded-sm disabled:opacity-50">
          {saving ? "Saving…" : "Save price"}
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-5">
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Vendors included in this bundle</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {vendors.map((v) => {
            const ok = status?.vendors?.[v.code]?.configured;
            return (
              <div key={v.code} className="flex items-center justify-between border border-zinc-200 rounded-sm px-3 py-2 text-sm">
                <div>
                  <div className="font-medium">{v.label}</div>
                  <div className="text-[10px] text-zinc-500 font-mono-plex">{v.code}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${ok ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-600"}`}>
                  {ok ? "Configured" : "No API key"}
                </span>
              </div>
            );
          })}
        </div>
        <div className="text-[11px] text-zinc-500 mt-3">
          API keys are managed in <code className="font-mono-plex">backend/.env</code> (IPQS_API_KEY, MAXMIND_*, HUNTER_API_KEY, GOOGLE_PLACES_API_KEY). Vendors without keys are silently skipped during a check.
        </div>
      </div>
    </div>
  );
}

function Table({ head, children }) {
  return (
    <div className="overflow-x-auto -mx-4 px-4">
      <table className="w-full text-sm min-w-[540px]">
        <thead>
          <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            {head.map((h, i) => <th key={i} className={i >= head.length - 2 ? "text-right py-2" : "text-left py-2"}>{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function ReportsTab() {
  const [tab, setTab] = useState("shipping");
  return (
    <div className="space-y-4">
      <div>
        <div className="font-display font-bold text-lg">Admin reports</div>
        <p className="text-sm text-zinc-600 mt-0.5 max-w-2xl">
          Generate and download the two operational reports SellePack needs.
          Both can be exported as CSV or sent automatically to your insurance
          partner end-of-day.
        </p>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-zinc-200">
        {[
          { id: "shipping",       l: "Shipping report" },
          { id: "insurance",      l: "Insurance send-form" },
          { id: "reconciliation", l: "Reconciliation" },
        ].map((t) => (
          <button key={t.id} data-testid={`reports-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === t.id ? "border-[var(--brand-accent)] text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === "shipping" && <ShippingReport />}
      {tab === "insurance" && <InsuranceReport />}
      {tab === "reconciliation" && <ReconciliationReport />}
    </div>
  );
}

function ReconciliationReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ book_number: "", paid_amount: "", carrier: "unisure", status: "paid", note: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/reports/insurance/reconciliation?days=${days}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);

  const submitClaim = async (e) => {
    e.preventDefault();
    if (!form.book_number || !form.paid_amount) {
      toast.error("Book # and Paid amount required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/insurance/claims", {
        book_number: form.book_number.trim(),
        paid_amount: parseFloat(form.paid_amount) || 0,
        carrier: form.carrier,
        status: form.status,
        note: form.note,
      });
      toast.success("Claim logged");
      setForm({ book_number: "", paid_amount: "", carrier: "unisure", status: "paid", note: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to log claim");
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-zinc-500 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>;
  if (!data) return null;
  const t = data.totals;
  const recovery = t.expected_total > 0 ? ((t.paid_total / t.expected_total) * 100).toFixed(1) : "—";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <ReportWindow days={days} setDays={setDays} />
        <div className="text-xs text-zinc-500">{data.window.start} → {data.window.end}</div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Expected (reported)" value={fmtUsd(t.expected_total)} icon={ReceiptText} />
        <Stat label="Paid by insurance" value={fmtUsd(t.paid_total)} icon={DollarSign} accent />
        <Stat label="Shortfall" value={fmtUsd(t.shortfall_total)} icon={AlertTriangle} warn={t.shortfall_total > 0} />
        <Stat label="Recovery rate" value={typeof recovery === "string" ? recovery : `${recovery}%`} icon={TrendingUp} />
      </div>

      <div className="grid sm:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-4">
          {t.missing_count > 0 && (
            <div className="bg-white border border-amber-300 rounded-sm p-4">
              <div className="font-display font-bold mb-2 text-amber-700">
                Missing claims ({t.missing_count}) — reported but no payment recorded
              </div>
              <Table head={["Shipped", "Book #", "Tracking", "Carrier", "Expected"]}>
                {data.missing.slice(0, 20).map((r) => (
                  <tr key={r.book_number} className="border-b border-zinc-100">
                    <td className="py-2 text-xs text-zinc-500">{(r.shipped_at || "").slice(0, 10)}</td>
                    <td className="font-mono-plex text-xs">{r.book_number}</td>
                    <td className="font-mono-plex text-xs">{r.tracking_number || "—"}</td>
                    <td className="text-xs">{r.carrier || "—"}</td>
                    <td className="text-right font-mono-plex font-bold text-amber-700">{fmtUsd(r.expected_amount)}</td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
          {t.underpaid_count > 0 && (
            <div className="bg-white border border-red-300 rounded-sm p-4">
              <div className="font-display font-bold mb-2 text-red-700">
                Under-paid claims ({t.underpaid_count})
              </div>
              <Table head={["Book #", "Carrier", "Expected", "Paid", "Shortfall"]}>
                {data.underpaid.slice(0, 20).map((r) => (
                  <tr key={r.book_number} className="border-b border-zinc-100">
                    <td className="font-mono-plex text-xs">{r.book_number}</td>
                    <td className="text-xs">{r.carrier || "—"}</td>
                    <td className="text-right font-mono-plex">{fmtUsd(r.expected_amount)}</td>
                    <td className="text-right font-mono-plex">{fmtUsd(r.paid_amount)}</td>
                    <td className="text-right font-mono-plex font-bold text-red-700">{fmtUsd(r.shortfall)}</td>
                  </tr>
                ))}
              </Table>
            </div>
          )}
          {t.missing_count === 0 && t.underpaid_count === 0 && (
            <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-6 text-emerald-900 text-sm inline-flex items-center gap-2">
              <Check size={16} /> All reported claims in this window are paid in full.
            </div>
          )}
        </div>

        <form data-testid="claim-entry-form" onSubmit={submitClaim} className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3 self-start">
          <div className="font-display font-bold">Log carrier payout</div>
          <p className="text-[11px] text-zinc-500">Record what the insurance partner actually paid out for a shipment. The reconciliation report updates instantly.</p>
          <label className="block text-xs">
            <div className="uppercase tracking-wider text-zinc-500 mb-1">Book #</div>
            <input data-testid="claim-book-number" value={form.book_number} onChange={(e) => setForm({ ...form, book_number: e.target.value })}
              placeholder="SP1234567" className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex" />
          </label>
          <label className="block text-xs">
            <div className="uppercase tracking-wider text-zinc-500 mb-1">Paid amount ($)</div>
            <input data-testid="claim-paid-amount" type="number" step="0.01" min="0" value={form.paid_amount}
              onChange={(e) => setForm({ ...form, paid_amount: e.target.value })}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm font-mono-plex" />
          </label>
          <label className="block text-xs">
            <div className="uppercase tracking-wider text-zinc-500 mb-1">Carrier partner</div>
            <select value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value })}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white">
              <option value="unisure">Unisure</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block text-xs">
            <div className="uppercase tracking-wider text-zinc-500 mb-1">Status</div>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white">
              <option value="paid">Paid</option>
              <option value="partial">Partial</option>
              <option value="denied">Denied</option>
              <option value="pending">Pending</option>
            </select>
          </label>
          <label className="block text-xs">
            <div className="uppercase tracking-wider text-zinc-500 mb-1">Note (optional)</div>
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm" />
          </label>
          <button data-testid="submit-claim" type="submit" disabled={saving}
            className="w-full brand-bg text-white py-2 rounded-sm text-sm disabled:opacity-50">
            {saving ? "Logging…" : "Log claim payout"}
          </button>
        </form>
      </div>
    </div>
  );
}

function ReportWindow({ days, setDays }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="text-xs uppercase tracking-wider text-zinc-500">Window</div>
      {[1, 7, 30, 90, 365].map((d) => (
        <button key={d} onClick={() => setDays(d)}
          className={`text-xs px-2.5 py-1 rounded-sm border ${days === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}>
          {d === 1 ? "Today" : `${d}d`}
        </button>
      ))}
    </div>
  );
}

async function downloadCsv(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Downloaded");
  } catch (e) {
    toast.error(e.response?.data?.detail || "Download failed");
  }
}

function ShippingReport() {
  const [days, setDays] = useState(30);
  const [channel, setChannel] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/reports/shipping?days=${days}&channel=${channel}`); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days, channel]);
  if (loading) return <div className="text-sm text-zinc-500 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>;
  if (!data) return null;
  const t = data.totals;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <ReportWindow days={days} setDays={setDays} />
          <div className="inline-flex items-center gap-1 border-l border-zinc-200 pl-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500">Channel</div>
            {[
              { v: "all", l: "All" },
              { v: "retail", l: "Retail" },
              { v: "wholesale", l: "Wholesale" },
            ].map((c) => (
              <button key={c.v} data-testid={`channel-filter-${c.v}`} onClick={() => setChannel(c.v)}
                className={`text-xs px-2.5 py-1 rounded-sm border ${channel === c.v ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}>
                {c.l}
              </button>
            ))}
          </div>
        </div>
        <button data-testid="download-shipping-csv" onClick={() => downloadCsv(`/admin/reports/shipping?days=${days}&channel=${channel}&format=csv`, `shipping_report_${channel}_${days}d.csv`)}
          className="brand-bg text-white text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1 hover:opacity-90">
          <Download size={12} /> Download CSV
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
        <Stat label="Shipments"    value={t.shipments} icon={Package} />
        <Stat label="GMV"          value={fmtUsd(t.gmv)} icon={DollarSign} />
        <Stat label="Surcharge"    value={fmtUsd(t.platform_surcharge)} icon={TrendingUp} accent />
        <Stat label="Wholesale"    value={t.wholesale ?? 0} icon={Users} />
        <Stat label="Retail"       value={t.retail ?? 0} icon={Users} />
        <Stat label="High value"   value={t.high_value} icon={Shield} warn={t.high_value > 0} />
      </div>
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <div className="font-display font-bold mb-3">By carrier</div>
        {!data.by_carrier?.length ? <div className="text-sm text-zinc-500">No data.</div> : (
          <Table head={["Carrier", "Shipments", "GMV", "Platform revenue"]}>
            {data.by_carrier.map((c, i) => (
              <tr key={i} className="border-b border-zinc-100">
                <td className="py-2">{c.carrier}</td>
                <td className="text-right font-mono-plex">{c.shipments}</td>
                <td className="text-right font-mono-plex">{fmtUsd(c.gmv)}</td>
                <td className="text-right font-mono-plex font-bold text-emerald-700">{fmtUsd(c.revenue)}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <div className="font-display font-bold mb-3">Recent shipments ({Math.min(data.shipments.length, 25)} of {data.shipments.length})</div>
        {!data.shipments.length ? <div className="text-sm text-zinc-500">No shipments in window.</div> : (
          <Table head={["Date", "Book #", "Tracking", "Carrier", "To", "Total"]}>
            {data.shipments.slice(0, 25).map((s) => (
              <tr key={s.shipment_id} className="border-b border-zinc-100">
                <td className="py-2 text-xs text-zinc-500">{(s.created_at || "").slice(0, 10)}</td>
                <td className="font-mono-plex text-xs">{s.book_number}</td>
                <td className="font-mono-plex text-xs">{s.tracking_number || "—"}</td>
                <td className="text-xs">{s.carrier_name}</td>
                <td className="text-xs text-zinc-600">{s.receiver?.city}, {s.receiver?.state} {s.receiver?.country}</td>
                <td className="text-right font-mono-plex font-bold">{fmtUsd(s.total)}</td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

function InsuranceReport() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/reports/insurance?days=${days}`); setData(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);
  const sendDaily = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/reports/insurance/send-daily");
      toast.success(`Insurance form sent (${data.status})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };
  if (loading) return <div className="text-sm text-zinc-500 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>;
  if (!data) return null;
  const t = data.totals;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <ReportWindow days={days} setDays={setDays} />
        <div className="flex gap-1">
          <button data-testid="download-insurance-csv" onClick={() => downloadCsv(`/admin/reports/insurance?days=${days}&format=csv`, `insurance_send_form_${days}d.csv`)}
            className="border border-zinc-300 text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1 hover:bg-zinc-50">
            <Download size={12} /> Download CSV
          </button>
          <button data-testid="send-daily-insurance" onClick={sendDaily} disabled={sending}
            className="brand-bg text-white text-xs px-3 py-1.5 rounded-sm inline-flex items-center gap-1 disabled:opacity-50">
            {sending ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />} Email partner now
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Insurable rows" value={t.rows} icon={ReceiptText} />
        <Stat label="Value of sale total" value={fmtUsd(t.value_of_sale_total)} icon={DollarSign} accent />
        <Stat label="High value over 15k" value={t.high_value_count} icon={Shield} warn={t.high_value_count > 0} />
        <Stat label="International" value={t.international_count} icon={ExternalLink} />
      </div>
      <div className="bg-white border border-zinc-200 rounded-sm p-4">
        <div className="font-display font-bold mb-3">Send-form rows (preview)</div>
        {!data.rows.length ? <div className="text-sm text-zinc-500">No insurable shipments in window.</div> : (
          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <th className="text-left py-2">Shipped</th>
                  <th className="text-left">Book #</th>
                  <th className="text-left">Tracking</th>
                  <th className="text-left">Carrier</th>
                  <th className="text-left">To</th>
                  <th className="text-left">Commodity</th>
                  <th className="text-right">Declared</th>
                  <th className="text-right">Sale value</th>
                  <th className="text-center">Flags</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.slice(0, 30).map((r) => (
                  <tr key={r.book_number} className="border-b border-zinc-100">
                    <td className="py-2 text-xs text-zinc-500">{(r.shipped_at || "").slice(0, 10)}</td>
                    <td className="font-mono-plex text-xs">{r.book_number}</td>
                    <td className="font-mono-plex text-xs">{r.tracking_number || "—"}</td>
                    <td className="text-xs">{r.carrier || "—"}</td>
                    <td className="text-xs text-zinc-600">{r.to_zip}{r.is_international ? ` · ${r.to_country}` : ""}</td>
                    <td className="text-xs">{r.commodity_category}</td>
                    <td className="text-right font-mono-plex">{fmtUsd(r.declared_value)}</td>
                    <td className="text-right font-mono-plex font-bold">{fmtUsd(r.value_of_sale)}</td>
                    <td className="text-center text-xs">
                      {r.high_value && <span className="px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 text-[10px] uppercase tracking-wider">{">$15k"}</span>}
                      {r.is_international && <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 text-[10px] uppercase tracking-wider">INTL</span>}
                      {r.signature_required && <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-700 text-[10px] uppercase tracking-wider">SIG</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function NotificationsTab() {  const [status, setStatus] = useState(null);
  useEffect(() => { api.get("/notifications/status").then(({ data }) => setStatus(data)).catch(() => {}); }, []);
  if (!status) return <div className="text-sm text-zinc-500 inline-flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Loading…</div>;
  const channels = status.channels || {};
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      {["email", "sms", "whatsapp"].map((ch) => {
        const c = channels[ch] || {};
        const ok = c.configured;
        return (
          <div key={ch} className={`border rounded-sm p-4 ${ok ? "bg-emerald-50 border-emerald-300" : "bg-zinc-50 border-zinc-300"}`}>
            <div className="text-xs uppercase tracking-wider text-zinc-500">{ch}</div>
            <div className="font-display font-bold mt-1">{c.provider || "—"}</div>
            <div className={`text-xs mt-2 ${ok ? "text-emerald-700" : "text-zinc-500"}`}>
              {ok ? "Live · keys configured" : "Not configured"}
            </div>
            {c.from && <div className="text-[11px] text-zinc-600 mt-1 truncate">From: {c.from}</div>}
          </div>
        );
      })}
      <Link to="/dashboard/notifications" className="sm:col-span-3 text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 underline">
        Configure preferences & send a test <ExternalLink size={11} />
      </Link>
    </div>
  );
}

function PricingTab() {
  const [pricing, setPricing] = useState({ markup_percent: 10, min_markup: 0.5 });
  useEffect(() => { api.get("/admin/pricing").then(({ data }) => setPricing(data)).catch(() => {}); }, []);
  const save = async () => {
    try { await api.put("/admin/pricing", pricing); toast.success("Pricing saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-sm p-5 max-w-md">
      <div className="font-display font-bold text-lg mb-1">Tenant default markup</div>
      <p className="text-xs text-zinc-500 mb-4">Applied to rates without a per-program surcharge override.</p>
      <div className="space-y-3">
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Markup %</label>
          <input data-testid="markup-percent" type="number" step="0.5" value={pricing.markup_percent}
            onChange={(e) => setPricing({ ...pricing, markup_percent: parseFloat(e.target.value) })}
            className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)]" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-zinc-500">Minimum markup ($)</label>
          <input data-testid="min-markup" type="number" step="0.01" value={pricing.min_markup}
            onChange={(e) => setPricing({ ...pricing, min_markup: parseFloat(e.target.value) })}
            className="mt-1 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-[var(--brand-accent)]" />
        </div>
        <button data-testid="save-pricing-btn" onClick={save} className="w-full brand-bg text-white px-4 py-2 rounded-sm hover:opacity-90">Save pricing</button>
      </div>
    </div>
  );
}

// -------------------- Wizards (modals) --------------------
function WizardShell({ title, step, total, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm" data-testid="wizard-shell">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-xl border border-zinc-200 max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Step {step} / {total}</div>
            <div className="font-display text-xl font-black tracking-tighter mt-0.5">{title}</div>
          </div>
          <button data-testid="wizard-close" onClick={onClose} className="text-zinc-400 hover:text-zinc-900"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-y-auto flex-1">{children}</div>
        <div className="p-4 border-t border-zinc-200 flex items-center justify-end gap-2">{footer}</div>
      </div>
    </div>
  );
}

function PartnerWizard({ onClose }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    commission_type: "percent", commission_rate: 20,
  });
  const [created, setCreated] = useState(null);
  const [saving, setSaving] = useState(false);

  const total = 3;
  const next = () => setStep((s) => Math.min(total, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));
  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/admin/partners", {
        ...form, commission_rate: parseFloat(form.commission_rate) || 0,
      });
      setCreated(data);
      toast.success("Partner created");
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };

  const referralUrl = created && typeof window !== "undefined"
    ? `${window.location.origin}/register?ref=${created.referral_code}` : "";

  return (
    <WizardShell title="Onboard a partner" step={step} total={total} onClose={onClose}
      footer={
        step === 1 ? (
          <>
            <button onClick={onClose} className="border border-zinc-300 px-4 py-2 rounded-sm text-sm">Cancel</button>
            <button data-testid="wizard-partner-next-1" disabled={!form.name || !form.email}
              onClick={next} className="brand-bg text-white px-4 py-2 rounded-sm text-sm disabled:opacity-50">Continue</button>
          </>
        ) : step === 2 ? (
          <>
            <button onClick={back} className="border border-zinc-300 px-4 py-2 rounded-sm text-sm">Back</button>
            <button data-testid="wizard-partner-create" disabled={saving} onClick={finish}
              className="brand-bg text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-1 disabled:opacity-50">
              {saving && <Loader2 className="animate-spin" size={14} />} Create partner
            </button>
          </>
        ) : (
          <button data-testid="wizard-partner-done" onClick={onClose} className="brand-bg text-white px-4 py-2 rounded-sm text-sm">Done</button>
        )
      }>
      {step === 1 && (
        <div className="space-y-3 text-sm">
          <Field label="Partner name" required>
            <input data-testid="wizard-partner-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2" placeholder="e.g., Acme Resellers" />
          </Field>
          <Field label="Email" required>
            <input data-testid="wizard-partner-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2" placeholder="partner@example.com" />
          </Field>
          <Field label="Company (optional)">
            <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2" />
          </Field>
        </div>
      )}
      {step === 2 && (
        <div className="space-y-4 text-sm">
          <Field label="Commission type">
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "percent", l: "Percentage", d: "% of every platform surcharge earned" },
                { v: "flat",    l: "Flat",       d: "$ per shipment booked" },
              ].map((o) => (
                <button key={o.v} data-testid={`wizard-partner-type-${o.v}`} onClick={() => setForm({ ...form, commission_type: o.v })}
                  className={`text-left border rounded-sm p-3 ${form.commission_type === o.v ? "border-[var(--brand-accent)] bg-blue-50" : "border-zinc-300 hover:bg-zinc-50"}`}>
                  <div className="font-display font-bold flex items-center gap-1">{o.l} {form.commission_type === o.v && <Check size={14} className="text-emerald-600" />}</div>
                  <div className="text-[11px] text-zinc-600 mt-1">{o.d}</div>
                </button>
              ))}
            </div>
          </Field>
          <Field label={form.commission_type === "percent" ? "Rate %" : "Rate $"}>
            <input data-testid="wizard-partner-rate" type="number" min="0" step="0.01" value={form.commission_rate}
              onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
              className="w-full border border-zinc-300 rounded-sm px-3 py-2 font-mono-plex" />
          </Field>
          <Field label="Login password (optional)">
            <input data-testid="wizard-partner-password" type="text" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Leave blank to skip — partner won't get a login"
              className="w-full border border-zinc-300 rounded-sm px-3 py-2 font-mono-plex" />
            <div className="text-[10px] text-zinc-500 mt-1">If set, the partner can sign in at /login with their email + this password and land in their own dashboard.</div>
          </Field>
          <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-xs">
            <b>Preview:</b> on a shipment with a $2.00 platform surcharge,{" "}
            <span className="font-mono-plex">
              {form.commission_type === "percent"
                ? fmtUsd((2 * (parseFloat(form.commission_rate) || 0)) / 100)
                : fmtUsd(parseFloat(form.commission_rate) || 0)}
            </span>{" "}
            is credited to the partner.
          </div>
        </div>
      )}
      {step === 3 && created && (
        <div className="space-y-3 text-sm">
          <div className="bg-emerald-50 border border-emerald-300 rounded-sm p-3 text-emerald-900 inline-flex items-center gap-2">
            <Check size={16} /> Partner <b>{created.name}</b> is live.
          </div>
          <Field label="Referral code">
            <div className="flex gap-2">
              <code className="flex-1 bg-zinc-900 text-white px-3 py-2 rounded-sm font-mono-plex text-base">{created.referral_code}</code>
              <button onClick={() => { navigator.clipboard.writeText(created.referral_code); toast.success("Copied"); }}
                className="border border-zinc-300 rounded-sm px-3"><Copy size={14} /></button>
            </div>
          </Field>
          <Field label="Referral link">
            <div className="flex gap-2">
              <input readOnly value={referralUrl} className="flex-1 border border-zinc-300 rounded-sm px-3 py-2 text-xs font-mono-plex bg-zinc-50" />
              <button onClick={() => { navigator.clipboard.writeText(referralUrl); toast.success("Copied"); }}
                className="border border-zinc-300 rounded-sm px-3"><Copy size={14} /></button>
            </div>
          </Field>
          <p className="text-xs text-zinc-500">Share this with the partner. Anyone who registers using this link will be attributed to them automatically; commissions accrue per shipment.</p>
        </div>
      )}
    </WizardShell>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}

// Lightweight wizards for carrier + fraud + first-run. They mostly link to
// existing flows so the admin always lands on a working configuration screen.
function CarrierWizard({ onClose }) {
  return (
    <WizardShell title="Connect a new carrier" step={1} total={1} onClose={onClose}
      footer={<Link to="/dashboard/admin/shipping" onClick={onClose}
        className="brand-bg text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-1">
          Open carrier setup <ArrowRight size={14} />
        </Link>}>
      <div className="space-y-3 text-sm">
        <p>To wire a new carrier slot (Ship 1–4) we use environment-backed API keys for the underlying aggregator:</p>
        <ul className="list-disc pl-5 text-zinc-600 space-y-1">
          <li><b>Ship 1</b> — EasyPost · set <code>EASYPOST_API_KEY</code></li>
          <li><b>Ship 2</b> — Webship/Smartlgstx · set <code>WEBSHIP_API_KEY</code> + <code>WEBSHIP_CUSTOMER_ID</code></li>
          <li><b>Ship 3</b> — Easyship · set <code>EASYSHIP_API_KEY</code></li>
          <li><b>Ship 4</b> — Combination · automatic, pick child slots in admin</li>
        </ul>
        <p className="text-zinc-500 text-xs">After setting the key, the slot flips to <b>Live</b> automatically. Then open the carrier admin to set markup %, min, flat add-on, and billing mode (wallet / saved card / carrier account).</p>
      </div>
    </WizardShell>
  );
}

function FraudWizard({ onClose }) {
  return (
    <WizardShell title="Add a fraud tool" step={1} total={1} onClose={onClose}
      footer={<button onClick={onClose} className="brand-bg text-white px-4 py-2 rounded-sm text-sm">Got it</button>}>
      <div className="space-y-3 text-sm">
        <p>The following fraud vendors are wired and ready when you provide an API key:</p>
        <div className="grid grid-cols-2 gap-2">
          {["IPQS (fraud score)","MaxMind (GeoIP risk)","Hunter (email risk)","Google Places (address)"].map((v) => (
            <div key={v} className="border border-zinc-200 rounded-sm p-2.5 text-xs">{v}</div>
          ))}
        </div>
        <p className="text-zinc-500 text-xs">More vendors (Sift / Ekata / Stripe Radar / EmailRep) can be wired on request — let me know which to prioritize and I'll add per-vendor surcharge billing too.</p>
      </div>
    </WizardShell>
  );
}

function FirstRunWizard({ onClose }) {
  const [step, setStep] = useState(1);
  const steps = [
    { t: "Confirm carriers", d: "Ship 1 + Ship 2 are live by default. Open the carrier admin to set per-program markup." , link: "/dashboard/admin/shipping" },
    { t: "Verify notifications", d: "Make sure Twilio and SendGrid keys are loaded.", link: "/dashboard/notifications" },
    { t: "Set insurance policy", d: "Configure Unisure limits, restricted ZIPs, banned terms.", link: "/dashboard/admin/insurance" },
    { t: "Onboard a partner (optional)", d: "Generate a referral code and share it with your first partner.", action: "partner" },
    { t: "Done", d: "You can revisit any step from the wizard rail.", link: null },
  ];
  const s = steps[step - 1];
  return (
    <WizardShell title="First-run setup" step={step} total={steps.length} onClose={onClose}
      footer={
        <>
          {step > 1 && <button onClick={() => setStep(step - 1)} className="border border-zinc-300 px-4 py-2 rounded-sm text-sm">Back</button>}
          {s.link ? <Link to={s.link} onClick={onClose} className="brand-bg text-white px-4 py-2 rounded-sm text-sm">Open</Link> :
            step < steps.length ? <button onClick={() => setStep(step + 1)} className="brand-bg text-white px-4 py-2 rounded-sm text-sm">Next</button> :
            <button onClick={onClose} className="brand-bg text-white px-4 py-2 rounded-sm text-sm">Finish</button>}
          {step < steps.length && s.link && <button onClick={() => setStep(step + 1)} className="text-xs text-zinc-500 underline">Skip</button>}
        </>
      }>
      <div className="space-y-2 text-sm">
        <div className="font-display font-bold text-lg">{s.t}</div>
        <p className="text-zinc-600">{s.d}</p>
      </div>
    </WizardShell>
  );
}

function fmtUsd(n) {
  if (n == null || isNaN(n)) return "—";
  return `$${Number(n).toFixed(2)}`;
}
